import { Outlet, type RouteObject } from 'react-router';

import { AppShell } from './app/AppShell';
import { RequireGroup } from './app/RequireGroup';
import { RequireSession } from './app/RequireSession';
import { NAV_ITEMS, type NavItem } from './app/navItems';
import Candidates from './pages/Candidates';
import Dashboard from './pages/Dashboard';
import Interview from './pages/Interview';
import { INTERVIEW_PATH } from './pages/Interview/interviewUrls';
import NotBuilt from './pages/NotBuilt';
import NotFound from './pages/NotFound';
import Outstanding from './pages/Outstanding';
import { OUTSTANDING_PATH } from './pages/Outstanding/outstandingUrls';
import PersonCard from './pages/PersonCard';
import QuestionBank from './pages/QuestionBank';
import { QUESTION_SET_PATH, QUESTIONS_PATH } from './pages/QuestionBank/questionBankUrls';
import QuestionSetScreen from './pages/QuestionBank/SetScreen';
import Invite from './pages/Invite';
import { INVITE_PATH } from './pages/Invite/inviteUrls';
import RouteError from './pages/RouteError';
import Settings from './pages/Settings';
import { SETTINGS_PATH } from './pages/Settings/settingsUrls';
import SignIn from './pages/SignIn';
import Vacancies from './pages/Vacancies';
import { VACANCIES_PATH } from './pages/Vacancies/vacancyUrls';

/**
 * Every §5 navigation destination whose screen this sprint does not build.
 *
 * **Derived from the sidebar's own table AND from the screens actually mounted**, so a navigation
 * item cannot become a dead link and a real screen cannot be shadowed by its own placeholder.
 * Adding an item to `NAV_ITEMS` mounts a route for it in the same move; mounting the real screen in
 * `built` below removes it from here in the same move.
 *
 * ⚠️ **The second half of that is what this function is for.** The filter was `to !== '/'`,
 * unconditionally, beside a comment claiming that giving a screen a route "takes it out of this
 * list" — it did not. Mounting `{ path: '/candidates', element: <Candidates/> }` beside the
 * placeholders would leave two sibling children with the same path; react-router breaks the tie by
 * definition order, the placeholders are spread first, `NotBuilt` wins, and the real screen never
 * renders while nothing fails. `app.test.tsx` asserts no address is mounted twice.
 *
 * What all of this replaces is the state found in a live browser: four items, two routes, and
 * react-router's developer error page three clicks from every Admin.
 */
function unbuiltNavItems(built: RouteObject[]): NavItem[] {
  // An `index` route has no `path` and answers the parent's address, which is `/`.
  const builtPaths = new Set(
    built.map((route) => (route.index ? '/' : route.path)).filter((path) => path !== undefined),
  );

  return NAV_ITEMS.filter((item) => !builtPaths.has(item.to));
}

/**
 * Sign-in, and everything behind the session — plus the two answers the app owes for an address it
 * has no screen for.
 *
 * Every screen mounts inside the same guard and the same shell, each in the sprint that wires it to
 * its API and replacing its placeholder in the same move. This paragraph named §6.5, §6.8 and §6.9 as
 * still to come until TASK-077 and §6.6/§6.7 until TASK-080; the interview address is below now, with
 * the answer it owes for a **blinded** payload built and the two readable bodies still placeholders
 * inside it (TASK-082 and TASK-085).
 *
 * ⚠️ **Nothing remains, and this paragraph said *"What remains is §6.10 Settings — the last address in
 * §5's table with no screen"* for four sprints after it stopped being true** (BUG-078, corrected
 * 2026-09-28). TASK-101 built that screen and mounted it below; **every §5 address has a screen of its
 * own**, and `unbuiltNavItems()` therefore returns nothing today.
 *
 * ⚠️ **The drift is the kind this file is otherwise proof against, which is the reason it is worth a
 * sentence rather than a silent fix.** `app.test.tsx` pins the placeholder inventory as an **equality**
 * against an empty list, so the *code* cannot quietly regrow a placeholder — and the prose four lines
 * up claimed one anyway, because no assertion reads prose. A claim about what does **not** exist yet is
 * the kind that rots without failing anything; if a screen is ever removed again, correct this
 * paragraph in the same edit that changes `built` below, and that equality's failure message will say
 * so.
 *
 * ⚠️ **§5's group table is enforced here, not only in the sidebar.** An item a group may not use is
 * not rendered, which says nothing about an address typed into the bar: `/questions` and
 * `/settings` served their screen to a Recruiter or an Interviewer who asked for them. Each route
 * below carries the groups its `NAV_ITEMS` row names **wherever that is fewer than all three**, and
 * `RequireGroup` answers everyone else with the same `NotFound` an unknown address gets — so a screen
 * built later inherits the gate rather than the hole, and the refusal never confirms that the screen
 * exists (§5; TASK-029's 404-not-403, see the comment in `RequireGroup`).
 *
 * ⚠️ **The qualifier is Sprint 19's** (2026-08-11): `/questions` is now §5's third item every group
 * has, so it carries no gate for the reason `/candidates` never did — a gate over all three could not
 * refuse anybody. Reading it as *"every route below is gated"* is what would put a `RequireGroup` back
 * on an address that must not have one.
 *
 * ⚠️ **Every branch ends in something designed.** `errorElement` catches a thrown render and the
 * `*` child catches an unknown URL; between them there is no path to react-router's own error page,
 * which is what an unmatched location produced before ("Unexpected Application Error!", no shell,
 * no way back). The catch-all is a **child of the guarded route on purpose**: an unknown address
 * renders inside the shell for someone signed in, so the navigation is still there, and sends
 * everyone else to sign-in with the address preserved, exactly as any other guarded screen does.
 */
export function buildRoutes(): RouteObject[] {
  /**
   * The screens that exist. A screen leaves the placeholder list by being added here.
   *
   * ⚠️ **`/candidates` carries no `RequireGroup`, and that is §5's own table rather than an
   * omission.** All three groups have the item, so there is nothing to hide from anybody and a gate
   * over all three would be a gate that can never refuse — the dashboard is mounted the same way for
   * the same reason. What an **Interviewer** may see *within* the screen is a different question and
   * is not this gate's: §6.3 gives them their assigned candidates only, the server narrows the
   * queryset (`services.visible_considerations`, `backend/tests/test_permission_matrix.py`), and a
   * reach past it answers 404, never 403 (backend TASK-029). `RequireGroup` decides what is
   * rendered, never what may be read.
   */
  const built: RouteObject[] = [
    { index: true, element: <Dashboard /> },
    { path: '/candidates', element: <Candidates /> },
    /**
     * §6.2a — every outstanding interview the viewer may see, and the destination §6.2 Section 1's
     * *View all →* points at (TASK-157). Section 1 shows five rows while stating a larger total, so
     * without this address the rest of the pile is unreachable from the screen whose subject it is.
     *
     * ⚠️ **No `RequireGroup`, and that is the permission matrix's own answer rather than an
     * omission** — the argument `/candidates`, `/candidates/:personId` and the interview address all
     * carry above. All three groups get `200` from `GET /api/v1/dashboard/outstanding/`, so a gate
     * over all three could never refuse. What differs per caller is the **queryset**, which the
     * server decides: an `Interviewer` is answered their own outstanding work through the same
     * `sees_every_candidate` predicate Section 1 already reads. `RequireGroup` decides what is
     * rendered, never what may be read.
     *
     * It is **not** a §5 navigation destination — it is reached from the dashboard, exactly as
     * `/candidates/:personId` and the interview address are — so it never appears in `NAV_ITEMS` and
     * `unbuiltNavItems()` has nothing to say about it.
     */
    { path: OUTSTANDING_PATH, element: <Outstanding /> },
    /**
     * §6.5's person card, **inside the same session guard and the same shell** as everything else
     * here, and with no `RequireGroup` for the same reason `/candidates` has none: §5 gives all three
     * groups the Candidates item, so a gate over all three could never refuse. What an **Interviewer**
     * may reach *within* the surface is the server's — their card carries only the considerations they
     * were assigned to (backend decision 7) and a reach past their assignment answers **404, never
     * 403** (backend TASK-029, `backend/tests/test_permission_matrix.py`), which the screen renders as
     * §8's Forbidden state. It is not a §5 navigation destination, so it never appears in
     * `NAV_ITEMS` and `unbuiltNavItems()` has nothing to say about it.
     */
    { path: '/candidates/:personId', element: <PersonCard /> },
    /**
     * §6.6 and §6.7 — **one address for both**, and which of them a reader gets is decided by the
     * payload rather than by the URL (`pages/Interview/index.tsx`; there is deliberately no
     * `/interviews/:id/edit`, because a second address would be a second place to reason about
     * whether feedback is still writable, which ADR 0008 settles once).
     *
     * ⚠️ **No `RequireGroup`, and that is the permission matrix's own answer rather than an
     * omission** — the same argument `/candidates` and `/candidates/:personId` carry above. All three
     * groups may call the route: `Route("get", INTERVIEW, None, (401, 404, 200, 200, 403, 403))` in
     * `backend/tests/test_permission_matrix.py`, over *(anonymous, Interviewer, Recruiter, Admin,
     * superuser, superuser-in-Admin)* — so a gate over all three could never refuse, and the 404 in
     * the Interviewer column is **reach, not group** (backend TASK-029: an interview outside their
     * assignment answers byte-for-byte what an id that never existed answers, because a 403 would
     * confirm it exists).
     *
     * ⚠️ **What differs between the groups is the queryset, never the status code**, and that is the
     * whole of ADR 0006 on this address: two readers of one URL legitimately receive **different
     * bodies** — a colleague's draft comes back as the blinded shape to everybody, `Admin` included. A group
     * gate would say nothing about that and could not enforce it; the shape of the payload does, and
     * the client's one job is to render whichever shape arrived (`isReadable`). `RequireGroup` decides
     * what is rendered, never what may be read.
     *
     * It is not a §5 navigation destination, so it never appears in `NAV_ITEMS` and
     * `unbuiltNavItems()` has nothing to say about it.
     */
    { path: INTERVIEW_PATH, element: <Interview /> },
    /**
     * §6.8's vacancy catalogue, **behind the groups its own `NAV_ITEMS` row names** — Admin and
     * Recruiter (§5's table: *"Vacancies · Admin ✓ · Recruiter ✓ (read-only) · Interviewer —"*). An
     * Interviewer typing the address gets the same `NotFound` an unknown address gets, byte for byte,
     * because a Forbidden state would tell them the screen exists (`RequireGroup`, §5's own closing
     * sentence).
     *
     * ⚠️ **The gate decides what is rendered, never what may be read**, and the evidence for the
     * refusal is server-side: `Route("get", ROLES, None, (401, 403, 200, 200, 403, 403))` in
     * `backend/tests/test_permission_matrix.py` — **the route's own Sprint 2 group gate**
     * (`read_access = require_group(GROUP_ADMIN, GROUP_RECRUITER)`), not TASK-029's candidate-surface
     * 404 rule. `vacancyGrid.test.tsx` asserts these groups are exactly the ones `NAV_ITEMS` gives the
     * item, so the two cannot drift apart.
     */
    {
      path: VACANCIES_PATH,
      element: (
        <RequireGroup groups={['Admin', 'Recruiter']}>
          <Vacancies />
        </RequireGroup>
      ),
    },
    /**
     * §6.9's question library — the tenant's, at `/questions`, and **reachable by all three groups**
     * (§5's table since Sprint 19: *"Questions · Admin ✓ · Recruiter ✓ · Interviewer ✓"*).
     *
     * ⚠️ **Two routes became one, and the gate went with the other** (2026-08-11, ADR 0018). This
     * address answered a **chooser** — the tenant's vacancies, each a link into its own bank — behind
     * `RequireGroup groups={['Admin']}`, and the bank itself was mounted at
     * `/vacancies/:vacancyId/questions` behind the same gate. A question belongs to the tenant now, so
     * `/questions` **is** the library, the nested address has no screen and no API behind it, there is
     * nothing left to choose between, and the ownership the chooser was built on — *"each vacancy owns
     * its bank"* — is **retracted**.
     *
     * ⚠️ **No `RequireGroup`, and that is §5's own table rather than an omission** — the argument
     * `/candidates` and the interview address already carry here. All three groups may call
     * this surface (`GET /api/v1/question-sets/` since ADR 0019; `GET /api/v1/questions/` until then),
     * so a gate over all three could never refuse. It stood on the opposite
     * fact — `Route("get", BANK, None, (401, 403, 403, 200, 403, 403))`, a **Recruiter 403 on the
     * bank** — and that row is gone with the route. What differs per caller is **what may be written**,
     * which is a fact about a row (`author_id`) and is decided where the row is rendered
     * (`pages/QuestionBank/setAccess.ts` — ⚠️ `questionBankAccess.ts` until 2026-09-24, when ADR 0019
     * left **one** write gate and it is the **set's**). `RequireGroup` decides what is rendered, never what
     * may be read.
     *
     * ⚠️ **It is a `NAV_ITEMS` row, so mounting it here is what takes `/questions` out of the
     * placeholder list.** `unbuiltNavItems()` derives the placeholders from the paths actually mounted
     * in `built`, which is why this entry has to spell the same address the sidebar does — and
     * `QUESTIONS_PATH` is the one constant both this and `pages/Dashboard/setupSteps.ts` read it from.
     */
    { path: QUESTIONS_PATH, element: <QuestionBank /> },
    /**
     * §6.9's **set composition screen** (Sprint 20) — one set, its questions and their order, at
     * `/questions/sets/:setId`.
     *
     * ⚠️ **No `RequireGroup`, on the same argument the address above carries**: all three groups may
     * call `GET /api/v1/question-sets/{id}/` (ADR 0016 decision 5(c) — an `Interviewer` curates sets
     * of their own), so a gate over all three could never refuse. What differs per caller is which
     * sets answer at all — the **reach** predicate, which is the server's — and a set outside a
     * reader's reach answers **404**, which the screen renders as §8's Not-found, byte-identical to
     * an address that does not exist. What differs again is what may be **written**, which is a fact
     * about the row (`author_id`) and is decided where the row is
     * (`pages/QuestionBank/setAccess.ts`).
     *
     * It is not a §5 navigation destination — it is reached from the *Sets* tab — so it never appears
     * in `NAV_ITEMS` and `unbuiltNavItems()` has nothing to say about it, exactly as
     * `/candidates/:personId` does not.
     */
    { path: QUESTION_SET_PATH, element: <QuestionSetScreen /> },
    /**
     * §6.10's Settings, **Admin only** (§5: *"Settings · Admin ✓ · Recruiter — · Interviewer —"*), and
     * the last address in that table to get a screen (TASK-101). `NotBuilt` stood here until this
     * sprint.
     *
     * ⚠️ **Mounting it is what takes `/settings` out of the placeholder list**, because
     * `unbuiltNavItems()` derives the placeholders from the paths actually mounted in `built` — so this
     * entry has to spell the same address `NAV_ITEMS` links to, and `SETTINGS_PATH` is where both read
     * it from. `app.test.tsx` asserts no address is mounted twice, which is the assertion that fails if
     * the two ever drift.
     *
     * ⚠️ **The groups are the screen's *write* groups, not its read groups**, and the asymmetry is
     * deliberate: `GET /api/v1/users/` admits a Recruiter (assignment needs it), while every write this
     * screen makes — the three staff writes and the two branding writes — is an `Admin`-only row in
     * `backend/tests/test_permission_matrix.py`. A Recruiter shown this screen would see a page whose
     * every control is a refusal, so §5's answer is that they do not have the address at all and
     * `RequireGroup` gives them the same `NotFound` an unknown address gets (never a Forbidden, which
     * would confirm the screen exists). The screen gates its own cache read on the same group, which is
     * a different question (`pages/Settings/index.tsx`).
     */
    {
      path: SETTINGS_PATH,
      element: (
        <RequireGroup groups={['Admin']}>
          <Settings />
        </RequireGroup>
      ),
    },
  ];

  return [
    { path: '/sign-in', element: <SignIn />, errorElement: <RouteError /> },
    /**
     * Redeeming a one-time invitation (TASK-101), and — like `/sign-in` — **outside `RequireSession`
     * and outside the shell**: the person arriving has no account they can sign in to yet, which is
     * what the token in the address is for. Wrapping it in the session gate would bounce every invited
     * colleague to the sign-in screen with a password they have not set.
     *
     * ⚠️ **The path is the one the server composes.** `InvitationOut.path` is `/invite#<token>`, built
     * in `apps/accounts/api.py`; `INVITE_PATH` is its client half, and a link whose screen did not
     * exist would be an invitation to a Not-found page — which is exactly what a live pass found this
     * task shipping before this route was added.
     *
     * ⚠️ **One static segment and no parameter, since BUG-028 (2026-08-05).** The token moved from the
     * path into the **fragment**, which no browser sends to a server, so nothing about it is routed —
     * the screen reads `location.hash`. Two consequences worth knowing before editing this line: a
     * link of the **old** shape (`/invite/<token>`) now matches no route of its own and falls to the
     * `*` child of `/`, which is **inside `RequireSession`** — so its holder lands on **sign-in**, not
     * on Not-found, and cannot use it, which is why links handed out before that release have to be
     * reissued (`PROD_DEPLOY_CHECKLIST.md`; asserted in `Invite/invite.test.tsx`, because two
     * documents said Not-found on the strength of the catch-all's name); and a `:token` parameter
     * must not be added back here for
     * "compatibility", because it is the path that reaches nginx's `$request` and the SPA server's own
     * access log.
     */
    { path: INVITE_PATH, element: <Invite />, errorElement: <RouteError /> },
    {
      path: '/',
      element: (
        <RequireSession>
          <AppShell>
            <Outlet />
          </AppShell>
        </RequireSession>
      ),
      errorElement: <RouteError />,
      children: [
        ...built,
        ...unbuiltNavItems(built).map((item) => ({
          path: item.to,
          element: (
            <RequireGroup groups={item.groups}>
              <NotBuilt />
            </RequireGroup>
          ),
        })),
        { path: '*', element: <NotFound /> },
      ],
    },
  ];
}
